/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';
import { primaryColorObj, secondaryColorObj } from '../../../../themes';

const CustomTabs = styled(Tabs)(() => ({
  '& .MuiTabs-scroller': {
    borderRadius: '70px',
    backgroundColor: secondaryColorObj.default,
    fontWeight: 500,
    marginBottom: 0,
    border: `1px solid ${primaryColorObj.default}`,

    '& .MuiTabs-flexContainer': {
      justifyContent: 'space-between',
    },

    '& .MuiTabs-indicator': {
      display: 'none',
    },

    '& .MuiButtonBase-root': {
      textTransform: 'none',
      opacity: '1 !important',
      fontSize: '1rem',
      fontWeight: 'bold',
      color: primaryColorObj.default,
      borderRight: `1px solid ${primaryColorObj.default}`,
      '&:last-child': {
        borderRight: 'none',
      },

      '&.Mui-selected': {
        backgroundColor: '#ffffff',
        color: secondaryColorObj.default,

        '&:hover': {
          backgroundColor: '#ffffff',
          opacity: 0.5,
        },
      },

      '&.Mui-disabled': {
        color: 'grey',
      },
    },
  },
  '& .MuiTabScrollButton-root': {
    paddingTop: '15px',
  },
}));

const TabMenu = ({
  handleChange,
  handleClick,
  currentTab,
  tabItems,
  hideOnTabs,
  sx,
  fixed,
  isTranslated,
  testId,
}) => {
  const { t } = useTranslation();

  return (
    !hideOnTabs.includes(tabItems[currentTab]?.label) && (
      <CustomTabs
        sx={sx}
        data-testid={testId}
        value={currentTab}
        onChange={(_, newValue) => {
          handleChange(newValue);
        }}
        variant={fixed ? 'fullWidth' : 'scrollable'}
        scrollButtons="auto"
      >
        {tabItems.length &&
          tabItems.map((tab, index) => (
            <Tab
              key={tab.label}
              sx={
                fixed
                  ? null
                  : { minWidth: 'fit-content', flex: 1, maxWidth: 'none' }
              }
              selected={currentTab === tab.label}
              label={
                isTranslated ? tab.label : t(`molecules.tab_menu.${tab.label}`)
              }
              data-testid={`${testId}-${index}`}
              datatesturl={tab.url}
              disabled={tab.disabled}
              onClick={() => {
                handleClick(index);
              }}
            />
          ))}
      </CustomTabs>
    )
  );
};

TabMenu.defaultProps = {
  hideOnTabs: [],
  sx: null,
  fixed: false,
  isTranslated: false,
  testId: 'tabTest',
  handleChange: () => null,
  handleClick: () => null,
};

TabMenu.propTypes = {
  handleChange: PropTypes.func,
  handleClick: PropTypes.func,
  currentTab: PropTypes.number.isRequired,
  tabItems: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  hideOnTabs: PropTypes.array,
  sx: PropTypes.object,
  fixed: PropTypes.bool,
  isTranslated: PropTypes.bool,
  testId: PropTypes.string,
};

export default TabMenu;
